// ModernToolProperties — 選択ツールのプロパティパネル (§9.1 / §9.6 全面新規).
//
// 方針 (docs/_wip/modern-layout.md §9.6 / §9.1 / §9.3、consult 2026-06-08):
//   - 常時表示 (progressive disclosure の最小): 太さ / 筆圧 太さ・濃さ toggle /
//     ペン専用 (palm rejection) / **マスク = モディファイア**
//     (none/normal/inverse + マスク色、§9.3 フォーク1B)。
//   - Classic 部品 (PenPressureConfig / MaskPanel / ToolPanel) は import しない
//     (= 全面新規、§9.6)。`application/penPressure` ストアと `maskHelpers` の純ロジック
//     は UI 非依存なので再利用 (props 経由)。
//   - マスク ON 中は canvas 枠/バッジのインジケータ (§9.3) を出す → これはレイアウト側
//     (ModernLayout) が `pen.mask` を見て canvas に出す。本パネルはマスク設定 UI を担う。
//
// 消しゴム選択中は筆圧「濃さ」/ マスクを隠す (消しゴムに無関係な軸を出さない、
// §9.1 progressive disclosure)。
//
// ## チップ撤去 + 再構成 (modern-brush-panel-2026-08-17.md §3 / §5、consult 2026-08-17)
//
// エアブラシ追加でパネルが縦に溢れた対策として、**ペン種チップ (「どのブラシか」) を
// 常設「ブラシ」パネル (`ModernBrushPanel`) へ切り出した**。本パネルは「そのブラシを
// どう調整するか」(常用: 太さ/不透明度/番手、ペン別: texture preset・airbrush
// grain/density) に専念する (§3 分担)。旧 `PenKindPicker` (チップ列 + ペン別パラメータの
// 合成) はもう使わず、**ペン別パラメータ本体 `PenParamsSection` を直接インポート**する
// (`onSelectPen` はチップと一緒に不要になったので本パネルの props から除いた)。
//
// **並べ替え (§5.1)**: 旧順は `chips → ペン別パラメータ → 番手 → 太さ → 不透明度 → …`
// で、ペン依存セクションが常用スライダより上にあったため、ペン切替のたびに太さ・
// 不透明度の画面位置が跳ねていた (マッスルメモリを壊す)。**常用 (太さ→不透明度→番手)
// を先、ペン別パラメータ (PenParamsSection) を後**に並べ替えた。
//
// **「詳細」disclosure (§5.2)**: 手ぶれ補正 / アンチエイリアス / スポイトの取得元は
// 低頻度行として既定 collapsed の折りたたみに入れる。3 つとも `PenSettingsModal` と
// 同一 source を共有済みで別の到達経路があるため、畳んでも発見性の損失はほぼない。
// **本コンポーネント自体は hook を持たない** (= `modernToolProperties*.test.tsx` が
// `ModernToolProperties(props)` を React レンダーパス外から直接呼んで戻り値を tree-walk
// する既存の様式のため、内部で `useState` を呼ぶと「Invalid hook call」になる)。開閉
// state は `ModernLayout` が保持し (`chatFontScale`/`sidebarScale` と同じローカル UI
// state パターン)、`detailsOpen`/`onDetailsToggle` として本パネルへ props で渡す。
// 中身は plain な `<details>`/`<summary>` (native disclosure、hook 不要) で表現する。

import { renderBrushSample } from '@oekaki/drawing-core';
import type { PenParams, StrokeMask, StrokePen, TexturePreset } from '@oekaki/protocol';
import { useEffect, useRef } from 'react';

import type { PenPressureSettings } from '../../../application/penPressure.js';
import type { DotByDotMode } from '../../../infrastructure/drawing/zoom.js';
import {
  isSpuitSampleMode,
  type SpuitSampleMode,
} from '../../../infrastructure/spuitSamplePreference.js';
import type { StabilizerLevel } from '../../../infrastructure/stabilizerPreference.js';
import { DOT_BY_DOT_DESCRIPTION, dotByDotStatusText } from '../../PenSettingsPanel.js';
import type { MaskSlotValue, MaskTypeUI } from '../classic/maskHelpers.js';
import { MAX_PEN_WIDTH } from '../classic/penWidthSlider.js';
import { PenParamsSection } from '../shared/PenParamsSection.js';
import { ModernWidthPresets } from './ModernWidthPresets.js';

export interface ModernToolPropertiesProps {
  /** 現在のペン種 (ブラシ/サインペン/消しゴム)。 */
  readonly pen: StrokePen;
  /** 現在の太さ (アクティブバケットの実効値)。 */
  readonly width: number;
  readonly onWidthChange: (w: number) => void;
  /**
   * 太さプリセット (§9.11 太さプリセット、consult 2026-09-29)。明示登録型
   * (Procreate/MediBang 型): クリックで `onWidthChange` に流すだけで登録値は変えない、
   * 「+」で現在値を登録、既定 3 個・最大 5 個。永続化・正規化 (昇順・重複除去・上限) は
   * `widthPresets.ts` / `infrastructure/widthPresetPreference.ts`、state は
   * `detailsOpen` と同じくここでは持たず `ModernLayout` が保持する
   * (`ModernToolProperties` 自体は hook を持たない、上部コメント参照)。
   */
  readonly widthPresets: readonly number[];
  readonly onAddWidthPreset: () => void;
  readonly onRemoveWidthPreset: (w: number) => void;
  readonly onResetWidthPresets: () => void;
  /**
   * 現在の不透明度 (= stroke.alpha、0..255 単一 alpha、§9.1 決定3)。
   * カラーパネルでなくここ (太さの近く) で調整する (consult 2026-06-08 ユーザ指摘)。
   */
  readonly alpha: number;
  readonly onAlphaChange: (alpha255: number) => void;
  /**
   * テクスチャ preset (texture-brush-design.md §10.1 / §11 step 3、2階層 UI)。
   * `pen==='texture'` のときだけ preset サムネセクションを出し、選択 preset を
   * ハイライトする。選択で以降の stroke が `penParams:{kind:'texture',preset}` を載せる。
   */
  readonly texturePreset: TexturePreset;
  readonly onSelectTexturePreset: (preset: TexturePreset) => void;
  /**
   * トーン番手 (density、texture-brush-design.md §12.2 段階1) / 間隔 (pitch、
   * phase11-light-pens-2026-08-21.md §4.5-§4.7、封印解除)。`pen==='tone'` のときだけ
   * `PenParamsSection` が内部でスライダ + segmented を出す (= 表現パリティ原則で
   * Classic にも同じ UI が出るよう、他ペン別パラメータと同じ相乗り方式に揃えた。旧実装は
   * ここに番手スライダをインラインで持っていたが、Classic 露出前提が無かったための
   * 独立コードパスだった)。選択で以降の stroke が
   * `penParams:{kind:'tone',pattern:'dot',density,pitch}` を載せる。
   */
  readonly toneDensity: number;
  readonly onToneDensityChange: (density: number) => void;
  readonly tonePitch: number;
  readonly onTonePitchChange: (pitch: number) => void;
  /**
   * エアブラシ grain/density (airbrush-design-2026-08-15.md §1.3 / §6)。
   * `pen==='airbrush'` のときだけ `PenParamsSection` が内部でスライダを出す
   * (= tone 番手・間隔と同じくこの部品自体には UI を持たず、そのまま横流しするだけ)。
   */
  readonly airbrushGrain: number;
  readonly onAirbrushGrainChange: (grain: number) => void;
  readonly airbrushDensity: number;
  readonly onAirbrushDensityChange: (density: number) => void;
  /**
   * クレヨン/パステルの目の粗さ (grain, 0..2、phase11-light-pens-2026-08-21.md
   * §2.6 / §6)。`pen==='crayon'|'pastel'` のときだけ `PenParamsSection` が内部で
   * segmented を出す (= tone 番手・airbrush grain/density と同じくそのまま横流しするだけ)。
   */
  readonly crayonGrain: number;
  readonly onCrayonGrainChange: (grain: number) => void;
  readonly pastelGrain: number;
  readonly onPastelGrainChange: (grain: number) => void;
  /**
   * ドットの間隔 (spacing, 1..200、掃引ステップ単位で px ではない、phase11-light-pens-
   * 2026-08-21.md §3.4/§3.5/§6)。`pen==='dot'` のときだけ `PenParamsSection` が内部で
   * スライダを出す (= grain/density 系と同じくそのまま横流しするだけ)。
   */
  readonly dotSpacing: number;
  readonly onDotSpacingChange: (spacing: number) => void;
  /** 筆圧設定 (太さ/濃さ toggle + min)。`application/penPressure` 再利用。 */
  readonly pressure: PenPressureSettings;
  readonly onPressureChange: (next: PenPressureSettings) => void;
  /** ペン専用モード (palm rejection)。 */
  readonly penOnlyMode: boolean;
  readonly onPenOnlyModeChange: (next: boolean) => void;
  /**
   * 「詳細」disclosure (手ぶれ補正 / アンチエイリアス / スポイトの取得元) の開閉状態
   * (modern-brush-panel-2026-08-17.md §5.2)。既定 collapsed、localStorage に永続。
   * 本コンポーネントは hook を持たない (= call-directly なユニットテスト様式のため)
   * ので、開閉 state は `ModernLayout` が保持し props で渡す
   * (`modernToolPropertiesDetailsPreference.ts` 参照)。
   */
  readonly detailsOpen: boolean;
  readonly onDetailsToggle: (open: boolean) => void;
  /**
   * 手ぶれ補正 (stroke stabilization、モディファイア §9.3 / §9.8)。
   * off/弱/中/強。入力平滑なので brush/eraser 両方に効く (= 消しゴム時も出す)。
   */
  readonly stabilizerLevel: StabilizerLevel;
  readonly onStabilizerChange: (level: StabilizerLevel) => void;
  /**
   * アンチエイリアス (AA、滑らかな線、モディファイア §9.3 / §9.9)。on/off トグル。
   * 既定 OFF (= 共通既定、consult 2026-06-13 続報でレイアウト別既定 Modern=on を
   * 撤廃 — 本パネルで常時見えるので発見性は足りる)。エッジ平滑なので brush/eraser 両方に
   * 効きうるが、現状 AA coverage は加算系ペン中心 (§9.9 決定3) → トグル自体は
   * 消しゴム時も隠さない (手ぶれ補正と同じ扱い、isEraser ガード外)。
   */
  readonly aaEnabled: boolean;
  readonly onAaChange: (next: boolean) => void;
  /** ドットバイドット表示 (自動 / しない)。値は共有 chrome。 */
  readonly dotByDotMode: DotByDotMode;
  readonly onDotByDotModeChange: (next: DotByDotMode) => void;
  /** この画面で自動が効く対象か。 */
  readonly dotByDotActive: boolean;
  /** マスク (モディファイア)。 */
  readonly mask: StrokeMask;
  readonly slot1: MaskSlotValue;
  readonly slot2: MaskSlotValue;
  readonly maskTypeUI: MaskTypeUI;
  readonly onMaskSlotClick: (slot: 0 | 1) => void;
  readonly onMaskTypeToggle: () => void;
  /**
   * スポイトの取得元モード (display/layer/layer-bg、spuit-sample-modes-2026-06-22)。
   * 値は共有 chrome (useRoomChrome) の per-user 設定で、「ペン」モーダル
   * (PenSettingsModal) と同じ単一 source = 双方が同じ値を読み書きして同期する。
   */
  readonly spuitSampleMode: SpuitSampleMode;
  readonly onSpuitSampleModeChange: (next: SpuitSampleMode) => void;
}

const rgb24ToHex = (rgb24: number): string =>
  `#${(rgb24 & 0xffffff).toString(16).padStart(6, '0')}`;

/**
 * 手ぶれ補正 segmented の選択肢 (§9.8 = off/弱/中/強)。値は engine の StabilizerLevel
 * enum、ラベルは Modern 表示名。off は「なし」と短く出す (= 既定状態が分かりやすい)。
 */
const STABILIZER_OPTIONS: ReadonlyArray<{
  readonly level: StabilizerLevel;
  readonly label: string;
}> = [
  { level: 'off', label: 'なし' },
  { level: 'weak', label: '弱' },
  { level: 'medium', label: '中' },
  { level: 'strong', label: '強' },
];

/**
 * inline 行 (ラベル左・コントロール右) で使う詰めた chip。segmented を 1 行に収める
 * ため `flex-1 min-w-0 overflow-hidden` で残り幅を等分し、狭ドック (最小 180px) でも
 * 横スクロールを出さない (= 極端に狭いときは文字がクリップされるだけ)。
 */
const inlineChipClass = (active: boolean): string =>
  `min-w-0 flex-1 overflow-hidden whitespace-nowrap rounded border px-1 py-0.5 text-center text-[10px] font-bold transition-colors ${
    active
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
  }`;

/**
 * inline 設定行のラベル。固定幅でコントロール開始位置を縦に揃える (= 見た目を整える)。
 * 最長ラベル「アンチエイリアス」(全角8) に合わせ、nowrap で折り返さない。
 */
const ROW_LABEL_CLASS =
  'w-20 shrink-0 select-none whitespace-nowrap text-[10px] leading-tight text-[var(--color-text-muted)]';

/** スポイトの取得元モードの選択肢 (PenSettingsPanel と同一文言、設計書 §2)。 */
const SPUIT_SAMPLE_MODE_OPTIONS: ReadonlyArray<{
  readonly value: SpuitSampleMode;
  readonly label: string;
}> = [
  { value: 'display', label: '表示色を取得（全て合成）' },
  { value: 'layer', label: 'レイヤーから取得' },
  { value: 'layer-bg', label: 'レイヤーと背景色から取得' },
];

export const ModernToolProperties = ({
  pen,
  width,
  onWidthChange,
  widthPresets,
  onAddWidthPreset,
  onRemoveWidthPreset,
  onResetWidthPresets,
  alpha,
  onAlphaChange,
  texturePreset,
  onSelectTexturePreset,
  toneDensity,
  onToneDensityChange,
  tonePitch,
  onTonePitchChange,
  airbrushGrain,
  onAirbrushGrainChange,
  airbrushDensity,
  onAirbrushDensityChange,
  crayonGrain,
  onCrayonGrainChange,
  pastelGrain,
  onPastelGrainChange,
  dotSpacing,
  onDotSpacingChange,
  pressure,
  onPressureChange,
  penOnlyMode,
  onPenOnlyModeChange,
  detailsOpen,
  onDetailsToggle,
  stabilizerLevel,
  onStabilizerChange,
  aaEnabled,
  onAaChange,
  dotByDotMode,
  onDotByDotModeChange,
  dotByDotActive,
  mask,
  slot1,
  slot2,
  maskTypeUI,
  onMaskSlotClick,
  onMaskTypeToggle,
  spuitSampleMode,
  onSpuitSampleModeChange,
}: ModernToolPropertiesProps): React.ReactNode => {
  const isEraser = pen === 'eraser';
  const maskActive = mask.type !== 'none' && mask.colors.length > 0;

  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-2"
      aria-label="ツールプロパティ"
    >
      {/* 消しゴム時のみツール名を出す (ブラシ時は「ブラシ」パネル側でどのブラシかが
          分かるので、ここでは太さ等の調整に専念する)。 */}
      {isEraser && (
        <header className="select-none text-[11px] font-bold text-[var(--color-text-muted)]">
          消しゴム
        </header>
      )}

      {/* ブラシサンプル (筆跡プレビュー)。選択中ペンの実際の描き味を本物の stroke
          レンダラ (drawing-core `renderBrushSample`) で「～」に描く。消しゴム中は隠す
          (描き味プレビュー対象外)。色は出さない (背景白 + 黒筆跡固定) ので
          描画色は渡さない (= 描き味だけを見せる、consult 2026-06-08)。 */}
      {!isEraser && (
        <BrushSamplePreview
          pen={pen}
          width={width}
          alpha={alpha}
          pressure={pressure}
          airbrushGrain={airbrushGrain}
          airbrushDensity={airbrushDensity}
          crayonGrain={crayonGrain}
          pastelGrain={pastelGrain}
          toneDensity={toneDensity}
          tonePitch={tonePitch}
          dotSpacing={dotSpacing}
        />
      )}

      {/* 常用セクション (§5.1 並べ替え): 太さ → 不透明度 → 番手。ペンを切り替えても
          この 3 行の画面位置が動かない (= マッスルメモリを壊さない、下のペン別
          パラメータより先に出す)。 */}

      {/* 太さ (サイズ) スライダ。1..MAX_PEN_WIDTH。 */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="select-none text-[10px] text-[var(--color-text-muted)]">太さ</span>
          <span className="select-none font-mono text-[10px] text-white">
            {Math.round(width)}px
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={MAX_PEN_WIDTH}
          step={1}
          value={Math.round(width)}
          onChange={(e) => onWidthChange(Number(e.currentTarget.value))}
          aria-label="太さ"
          data-manual="draw.size"
          className="w-full accent-[var(--color-accent)]"
        />
        {/* 太さプリセット (§9.11): クリックで適用するだけ、スライダでは登録値を
            変えない (= 上の <input type=range> と役割分担、明示登録型)。 */}
        <div className="mt-1">
          <ModernWidthPresets
            presets={widthPresets}
            currentWidth={width}
            onSelect={onWidthChange}
            onAdd={onAddWidthPreset}
            onRemove={onRemoveWidthPreset}
            onReset={onResetWidthPresets}
          />
        </div>
      </div>

      {/* 不透明度 (= stroke.alpha 単一 alpha、§9.1 決定3)。太さの直下に置く
          (= 太さと一緒に調整することが多い、consult 2026-06-08 ユーザ指摘)。
          値域は 0..255 だが表示は % が自然なので Math.round(alpha/255*100)% で出す
          (太さが px 表示なのと揃える)。alpha=0 (完全透明) は無意味なので下限 1。 */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="select-none text-[10px] text-[var(--color-text-muted)]">不透明度</span>
          <span className="select-none font-mono text-[10px] text-white">
            {Math.round((alpha / 255) * 100)}%
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={255}
          step={1}
          value={Math.round(alpha)}
          onChange={(e) => onAlphaChange(Number(e.currentTarget.value))}
          aria-label="不透明度"
          data-manual="draw.opacity"
          className="w-full accent-[var(--color-accent)]"
        />
      </div>

      {/* ペン別パラメータ (airbrush grain・density / tone 番手・間隔 / dot 間隔 スライダ)。
          共有 `PenParamsSection`
          (Modern / Classic 両用、modern-brush-panel-2026-08-17.md §8/§9-1) に集約済。
          どのブラシかは常設「ブラシ」パネル (`ModernBrushPanel`) 側で選ぶので、ここは
          選択済みペンの調整だけを担う (= チップは出さない、§3 分担)。
          常用セクションより後ろに置く (§5.1)。消しゴム中は隠す。
          `showTexturePreset={false}`: テクスチャの4プリセットは `ModernBrushPanel` の
          item 行へ昇格済みなので、ここで preset サムネを二重表示しない (実機フィードバック
          2026-08-17、`texturePreset`/`onSelectTexturePreset` 自体は無害な横流しとして
          残す — Classic 用 `PenKindPicker` と同じ props 形を保つため)。 */}
      {!isEraser && (
        <PenParamsSection
          pen={pen}
          showTexturePreset={false}
          texturePreset={texturePreset}
          onSelectTexturePreset={onSelectTexturePreset}
          airbrushGrain={airbrushGrain}
          onAirbrushGrainChange={onAirbrushGrainChange}
          airbrushDensity={airbrushDensity}
          onAirbrushDensityChange={onAirbrushDensityChange}
          crayonGrain={crayonGrain}
          onCrayonGrainChange={onCrayonGrainChange}
          pastelGrain={pastelGrain}
          onPastelGrainChange={onPastelGrainChange}
          toneDensity={toneDensity}
          onToneDensityChange={onToneDensityChange}
          tonePitch={tonePitch}
          onTonePitchChange={onTonePitchChange}
          dotSpacing={dotSpacing}
          onDotSpacingChange={onDotSpacingChange}
        />
      )}

      {/* 筆圧 (太さ / 濃さ toggle + min)。`application/penPressure` 再利用。
          消しゴムは「濃さ」を出さない (薄く消す機能はない)。 */}
      <div data-manual="draw.pressure">
        <p className="mb-1 select-none text-[10px] text-[var(--color-text-muted)]">筆圧</p>
        <PressureRow
          label="太さ"
          enabled={pressure.widthEnabled}
          min={pressure.widthMin}
          onToggle={() => onPressureChange({ ...pressure, widthEnabled: !pressure.widthEnabled })}
          onMinChange={(v) => onPressureChange({ ...pressure, widthMin: v })}
        />
        {!isEraser && (
          <PressureRow
            label="濃さ"
            enabled={pressure.alphaEnabled}
            min={pressure.alphaMin}
            onToggle={() => onPressureChange({ ...pressure, alphaEnabled: !pressure.alphaEnabled })}
            onMinChange={(v) => onPressureChange({ ...pressure, alphaMin: v })}
          />
        )}
      </div>

      {/* ペン専用 (palm rejection)。inline 行: ラベル左・チェック+補足右
          (2026-06-27 縦詰め)。補足「タッチ誤爆防止」= 手のひらツール (pan) と語が
          被らない表現 (consult 2026-06-08)。狭ドックでは補足を truncate する。 */}
      <label className="flex select-none items-center gap-2 text-[11px] text-white">
        <span className={ROW_LABEL_CLASS}>ペン専用</span>
        <span className="flex min-w-0 flex-1 items-center gap-1">
          <input
            type="checkbox"
            checked={penOnlyMode}
            onChange={(e) => onPenOnlyModeChange(e.currentTarget.checked)}
            className="h-3.5 w-3.5 shrink-0 accent-[var(--color-accent)]"
            data-manual="draw.pen-only"
          />
          <span className="truncate text-[9px] text-[var(--color-text-muted)]">
            (タッチ誤爆防止)
          </span>
        </span>
      </label>

      {/* マスク = モディファイア (§9.3 フォーク1B)。inline 行 (2026-06-27 縦詰め): ラベル左・
          [マスク色 ×2 + 種別トグル + ON バッジ] 右。狭ドックでは flex-wrap でスウォッチを
          副行に折り返して横スクロールを防ぐ (= 破綻させない範囲で最大限詰める)。
          消しゴム中は隠す。頻用モディファイアなので「詳細」disclosure には入れず常時
          表示する (§5.2、低頻度 3 行だけを畳む)。 */}
      {!isEraser && (
        <div className="flex items-center gap-2" data-manual="color.mask">
          <span className={ROW_LABEL_CLASS}>マスク</span>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {/* 2 slot (色を握る or 未設定)。クリックで現在描画色を握る/解除。 */}
            {([0, 1] as const).map((slot) => {
              const v: MaskSlotValue = slot === 0 ? slot1 : slot2;
              return (
                <button
                  key={slot}
                  type="button"
                  aria-label={`マスク色 ${slot + 1}${v === null ? ' (未設定)' : ''}`}
                  title={v === null ? 'クリックで現在色をマスクに設定' : 'クリックで解除'}
                  onClick={() => onMaskSlotClick(slot)}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-[var(--color-border)] text-[11px] text-[var(--color-text-muted)]"
                  style={v === null ? undefined : { backgroundColor: rgb24ToHex(v) }}
                >
                  {v === null ? '✕' : ''}
                </button>
              );
            })}
            {/* type toggle (normal/inverse)。 */}
            <button
              type="button"
              onClick={onMaskTypeToggle}
              aria-label={`マスク種別: ${maskTypeUI === 'normal' ? '通常' : '反転'}`}
              className="shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-2 py-0.5 text-[10px] font-bold text-white hover:bg-[var(--color-bg)]"
            >
              {maskTypeUI === 'normal' ? '通常' : '反転'}
            </button>
            {maskActive && (
              <span className="shrink-0 rounded bg-[var(--color-accent)] px-1 text-[9px] font-bold text-black">
                ON
              </span>
            )}
          </div>
        </div>
      )}

      {/* 「詳細」disclosure (modern-brush-panel-2026-08-17.md §5.2): 手ぶれ補正 / AA /
          スポイトの取得元は低頻度行として既定 collapsed に畳む。3 つとも
          `PenSettingsModal` と同一 source を共有済で別の到達経路が既にあるため、畳んでも
          発見性の損失はほぼない。native `<details>`/`<summary>` を使う (= キーボード
          操作・スクリーンリーダーの disclosure セマンティクスが標準で付き、独自 ARIA
          実装が不要)。開閉 state は hook を持たない本コンポーネントの外
          (`ModernLayout`) が保持し props (`detailsOpen`/`onDetailsToggle`) で渡す
          (= 上部コメント参照)。`<details>` は non-open でも children は DOM ツリーに
          残る (native の視覚的な折りたたみ、テストで tree-walk しても要素は見つかる)。 */}
      <details
        open={detailsOpen}
        onToggle={(e) => onDetailsToggle(e.currentTarget.open)}
        className="rounded border border-[var(--color-border)]"
      >
        <summary className="cursor-pointer select-none list-none rounded px-1 py-0.5 text-[10px] font-bold text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)]">
          詳細 (手ぶれ補正 / アンチエイリアス / スポイトの取得元)
        </summary>
        <div className="flex flex-col gap-2 p-1 pt-2">
          {/* 手ぶれ補正 = モディファイア (§9.3 / §9.8)。off/弱/中/強の segmented。
              入力平滑なので brush/eraser 両方に効く → **消しゴム時も隠さない**
              (= マスク・濃さ筆圧と違い、isEraser ガード外に置く)。segmented は
              <button aria-pressed> の toggle 群 (radio role は biome
              useSemanticElements が <input type=radio> を要求するため不採用、
              `ModernBrushPanel` のバケット選択と同じ回避策)。グループ名は
              fieldset/legend で与える。 */}
          <fieldset className="m-0 flex items-center gap-2 border-0 p-0" aria-label="手ぶれ補正">
            <legend className={`float-none ${ROW_LABEL_CLASS}`}>手ぶれ補正</legend>
            <div className="flex min-w-0 flex-1 gap-1">
              {STABILIZER_OPTIONS.map((opt) => {
                const active = stabilizerLevel === opt.level;
                return (
                  <button
                    key={opt.level}
                    type="button"
                    aria-pressed={active}
                    aria-label={`手ぶれ補正 ${opt.label}`}
                    onClick={() => onStabilizerChange(opt.level)}
                    className={inlineChipClass(active)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* アンチエイリアス (AA、§9.3 / §9.9)。on/off の segmented トグル。手ぶれ補正と
              同じく入力非依存のモディファイアなので **消しゴム時も隠さない**
              (isEraser ガード外)。2026-06-13 (表現パリティ §1.1) で tri-state 化: 値は
              共有 chrome の実効値で、両レイアウトの上部バー「ペン」→ PenSettingsModal が
              同じ値を読み書きする。未設定の既定は共通 off。 */}
          <fieldset
            className="m-0 flex items-center gap-2 border-0 p-0"
            aria-label="アンチエイリアス"
          >
            <legend className={`float-none ${ROW_LABEL_CLASS}`}>アンチエイリアス</legend>
            <div className="flex min-w-0 flex-1 gap-1">
              <button
                type="button"
                aria-pressed={!aaEnabled}
                aria-label="アンチエイリアス なし"
                onClick={() => onAaChange(false)}
                className={inlineChipClass(!aaEnabled)}
              >
                なし
              </button>
              <button
                type="button"
                aria-pressed={aaEnabled}
                aria-label="アンチエイリアス あり"
                onClick={() => onAaChange(true)}
                className={inlineChipClass(aaEnabled)}
              >
                あり
              </button>
            </div>
          </fieldset>

          {/* ドットバイドット表示 (自動 / しない)。AA と同じ segmented。説明は title と
              下の一言 (狭いので 1 行)。 */}
          <fieldset
            className="m-0 flex items-center gap-2 border-0 p-0"
            aria-label="ドットバイドット表示"
            title={DOT_BY_DOT_DESCRIPTION}
          >
            <legend className={`float-none ${ROW_LABEL_CLASS}`}>ドットバイドット</legend>
            <div className="flex min-w-0 flex-1 gap-1">
              {(['auto', 'off'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={dotByDotMode === m}
                  aria-label={`ドットバイドット表示 ${m === 'auto' ? '自動' : 'しない'}`}
                  onClick={() => onDotByDotModeChange(m)}
                  className={inlineChipClass(dotByDotMode === m)}
                >
                  {m === 'auto' ? '自動' : 'しない'}
                </button>
              ))}
            </div>
          </fieldset>
          <p className="m-0 text-[10px] text-[var(--color-text-muted)]">
            {DOT_BY_DOT_DESCRIPTION}
            {dotByDotStatusText(dotByDotMode, dotByDotActive)}
          </p>

          {/* スポイトの取得元 (3 択、spuit-sample-modes-2026-06-22)。値は共有 chrome の
              per-user 設定で、「ペン」モーダル (PenSettingsModal) の同名 select と同じ
              単一 source = 双方が同期する (新 state を作らない)。オプション文言が長いので
              select は flex-1 + min-w-0 で幅を抑え、truncate して横スクロールを防ぐ。
              挙動 (3 モード) は不変。 */}
          <div className="flex items-center gap-2">
            <span className={ROW_LABEL_CLASS}>スポイトの取得元</span>
            <select
              aria-label="スポイトの取得元"
              value={spuitSampleMode}
              onChange={(e) => {
                if (isSpuitSampleMode(e.currentTarget.value)) {
                  onSpuitSampleModeChange(e.currentTarget.value);
                }
              }}
              className="min-w-0 flex-1 truncate rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-1 py-0.5 text-[10px] text-white"
            >
              {SPUIT_SAMPLE_MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </details>
    </section>
  );
};

/**
 * ブラシサンプル (筆跡プレビュー)。選択中ペンの描き味を **本物の stroke レンダラ**
 * (drawing-core `renderBrushSample`) で「～」(緩いサイン波 + 固定の入り抜き圧力) に
 * 描く。サーバ snapshot と同じ描画コアを通すので bit-perfect (独自エンジン訴求)。
 *
 * - 反映軸: ペン種 (brush 累乗 build-up / flat uniform) / 太さ / 不透明度 /
 *   筆圧 (太さ・濃さの入り抜き、固定圧力プロファイル × 現在の `pressure` 設定)。
 * - 色は出さない: 背景=白 / 筆跡=黒固定 (= 描き味だけを見せる、色はカラーパネル +
 *   スウォッチで分かる、consult 2026-06-08)。よって描画色は受けない。
 * - 再描画: pen / width / alpha / pressure が変わったときだけ
 *   (= 毎フレームでなく設定変化時のみ、依存配列で制御)。
 * - 描画は worker/offscreen 非依存で同期完結 (= 軽量・都度再生成)。
 *
 * canvas の論理解像度はパネル幅に依らず固定 (PREVIEW_W × PREVIEW_H)。CSS で全幅に
 * 伸ばす (= レイアウト幅変化で再生成しないで済む / putImageData は論理 px 基準)。
 *
 * ## airbrush の `penParams` (2026-08 バグ修正)
 *
 * `resolveStampRenderer` (drawing-core `StampRenderer.ts`) は `pen==='airbrush'` かつ
 * `penParams` 省略時に `createNoopStampRenderer()` を返す (= texture の紙 preset /
 * tone の density 0.5 と違い airbrush にフォールバックは存在しない、synthesized seed
 * だと全送信者で同一絵になってしまうため意図的に禁止、airbrush-design-2026-08-15.md
 * §4-3)。よってプレビューでも `pen==='airbrush'` のときは `penParams` を必ず渡す。
 * `seed` は **このプレビュー専用の固定値** (`PREVIEW_AIRBRUSH_SEED`)。stroke 本番の
 * seed は毎ストローク `crypto.getRandomValues` で引き直すが、プレビューで同様に毎
 * render 引き直すと「設定を変えていないのにスウォッチがチラつく」ことになるため固定
 * する (プレビューは wire に載らないので決定論要件の対象外だが、UX 上「同じ設定なら
 * 同じ絵」は必要)。`grain` / `density` はスライダの現在値をそのまま渡す (= 粒の粗さ・
 * 密度が目で確認できることがこのスウォッチの価値)。
 *
 * ## クレヨン/パステルの `penParams` (phase11-light-pens-2026-08-21.md §2.6)
 *
 * crayon/pastel は airbrush と違い `penParams` 省略時も安全なフォールバック (既定
 * grain=1) を持つので必須ではないが、**省略すると目の粗さ segmented を変えてもプレビュー
 * が変化しない** (= 常に既定 grain の絵のまま) UX 上のデグレになる。segmented がこの
 * スウォッチの直上にある以上「動かしたら見た目が変わる」が期待挙動なので、airbrush と
 * 同様に現在値を明示的に渡す。
 *
 * ## トーン/ドットの `penParams` (phase11-light-pens-2026-08-21.md §3/§4、封印解除)
 *
 * tone は `pitch` を省略すると `TONE_DEFAULT_PITCH`(=4、pre-pitch レコードの replay
 * 忠実性のためだけの値) にフォールバックし、`4` は柄として成立しない (§4.3/§4.4) ので
 * 省略すると番手・間隔を動かしてもプレビューが崩れたまま変化する/見えない。dot も
 * spacing を省略すると `DOT_DEFAULT_SPACING`(=8、UI 既定と同じ) にフォールバックする
 * ため必須ではないが、間隔スライダの変更を反映するため crayon/pastel と同様に明示する。
 */
const PREVIEW_W = 220 as const;
const PREVIEW_H = 40 as const;

/**
 * airbrush プレビュー専用の固定 seed。stroke.start の実 seed と無関係 (プレビューは
 * wire に載らない使い捨て描画)。設定が同じなら常に同じ絵になることだけを担保する。
 */
const PREVIEW_AIRBRUSH_SEED = 0x5eed_1234 as const;

const BrushSamplePreview = ({
  pen,
  width,
  alpha,
  pressure,
  airbrushGrain,
  airbrushDensity,
  crayonGrain,
  pastelGrain,
  toneDensity,
  tonePitch,
  dotSpacing,
}: {
  readonly pen: StrokePen;
  readonly width: number;
  readonly alpha: number;
  readonly pressure: PenPressureSettings;
  /** `pen==='airbrush'` のときだけ使う (プレビュー penParams 構築用)。 */
  readonly airbrushGrain: number;
  readonly airbrushDensity: number;
  /** `pen==='crayon'` のときだけ使う (プレビュー penParams 構築用)。 */
  readonly crayonGrain: number;
  /** `pen==='pastel'` のときだけ使う (プレビュー penParams 構築用)。 */
  readonly pastelGrain: number;
  /** `pen==='tone'` のときだけ使う (プレビュー penParams 構築用)。 */
  readonly toneDensity: number;
  readonly tonePitch: number;
  /** `pen==='dot'` のときだけ使う (プレビュー penParams 構築用)。 */
  readonly dotSpacing: number;
}): React.ReactNode => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // airbrush はフォールバックが無い (上記コメント参照) ので明示的に penParams を
    // 埋める。crayon/pastel/tone/dot はフォールバック自体は安全だが、目の粗さ/番手/間隔
    // の変更がプレビューに反映されるよう同様に明示する (上部コメント参照)。他ペンは
    // 従来どおり未指定 (= drawing-core 側の既定フォールバックに委ねる、texture=紙 /
    // brush・flat・marker は penParams 自体を使わない)。
    const penParams: PenParams | undefined =
      pen === 'airbrush'
        ? {
            kind: 'airbrush',
            seed: PREVIEW_AIRBRUSH_SEED,
            grain: airbrushGrain,
            density: airbrushDensity,
          }
        : pen === 'crayon'
          ? { kind: 'crayon', grain: crayonGrain }
          : pen === 'pastel'
            ? { kind: 'pastel', grain: pastelGrain }
            : pen === 'tone'
              ? { kind: 'tone', pattern: 'dot', density: toneDensity, pitch: tonePitch }
              : pen === 'dot'
                ? { kind: 'dot', spacing: dotSpacing }
                : undefined;
    const sample = renderBrushSample({
      width: PREVIEW_W,
      height: PREVIEW_H,
      pen,
      penWidth: Math.max(1, Math.round(width)),
      alpha,
      pressure: {
        widthEnabled: pressure.widthEnabled,
        widthMin: pressure.widthMin,
        alphaEnabled: pressure.alphaEnabled,
        alphaMin: pressure.alphaMin,
      },
      penParams,
    });
    // drawing-core は不透明な白地 + 黒筆跡の RGBA を返すので putImageData で直貼り
    // (= 背景白なので clearRect 不要、白地がパネルに載る)。
    const imageData = new ImageData(sample.rgba, sample.width, sample.height);
    ctx.putImageData(imageData, 0, 0);
  }, [
    pen,
    width,
    alpha,
    pressure.widthEnabled,
    pressure.widthMin,
    pressure.alphaEnabled,
    pressure.alphaMin,
    airbrushGrain,
    airbrushDensity,
    crayonGrain,
    pastelGrain,
    toneDensity,
    tonePitch,
    dotSpacing,
  ]);

  return (
    <div className="overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)]">
      {/* CSS で全幅に引き伸ばすが、論理解像度は固定 (PREVIEW_W × PREVIEW_H)。
          aria-label は role を持つ canvas に付与 (= div は role なしで aria 非対応)。 */}
      <canvas
        ref={canvasRef}
        width={PREVIEW_W}
        height={PREVIEW_H}
        role="img"
        aria-label="ブラシのサンプル"
        className="block h-10 w-full"
      />
    </div>
  );
};

/** 筆圧 1 軸 (ON/OFF + 最低倍率スライダ)。`application/penPressure` の値域 0..1。 */
const PressureRow = ({
  label,
  enabled,
  min,
  onToggle,
  onMinChange,
}: {
  readonly label: string;
  readonly enabled: boolean;
  readonly min: number;
  readonly onToggle: () => void;
  readonly onMinChange: (v: number) => void;
}): React.ReactNode => (
  <div className="mb-1 flex items-center gap-2">
    <label className="flex w-14 shrink-0 select-none items-center gap-1 text-[10px] font-bold text-white">
      <input
        type="checkbox"
        checked={enabled}
        onChange={onToggle}
        className="h-3 w-3 accent-[var(--color-accent)]"
        aria-label={`${label}筆圧 ON/OFF`}
      />
      <span>{label}</span>
    </label>
    <input
      type="range"
      min={0}
      max={1}
      step={0.05}
      value={min}
      disabled={!enabled}
      onChange={(e) => onMinChange(Number(e.currentTarget.value))}
      aria-label={`${label}筆圧の最低倍率`}
      className="min-w-0 flex-1 accent-[var(--color-accent)] disabled:opacity-40"
    />
  </div>
);
